'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Filters {
  type: string;
  status: string;
  minBeds: number;
  maxPrice: number;
  verified: boolean;
}

interface Props {
  filters: Filters;
  onChange: (f: Filters) => void;
}

const propertyTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'studio', label: 'Studio' },
  { value: 'office', label: 'Office' },
];

const bedroomOptions = [
  { value: 0, label: 'Any' },
  { value: 1, label: '1+' },
  { value: 2, label: '2+' },
  { value: 3, label: '3+' },
  { value: 4, label: '4+' },
  { value: 5, label: '5+' },
];

const priceRanges = [
  { value: Infinity, label: 'Any Price' },
  { value: 2000000, label: 'Up to AED 2M' },
  { value: 5000000, label: 'Up to AED 5M' },
  { value: 10000000, label: 'Up to AED 10M' },
  { value: 20000000, label: 'Up to AED 20M' },
];

const amenities = ['Swimming Pool', 'Gym', 'Parking', 'Balcony', 'Sea View', 'Maid Room', 'Study Room', 'Private Garden', 'Smart Home', 'Central A/C'];

export default function FiltersSidebar({ filters, onChange }: Props) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['type', 'status', 'beds', 'price']));

  const toggle = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <div className="border-b border-border pb-4 mb-4">
      <button
        onClick={() => toggle(id)}
        className="flex items-center justify-between w-full text-sm font-semibold text-foreground mb-3 hover:text-primary transition-colors"
      >
        {title}
        <Icon name={expandedSections.has(id) ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-muted-foreground" />
      </button>
      {expandedSections.has(id) && <div>{children}</div>}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-card sticky top-24">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-foreground text-sm">Filters</h3>
        <button
          onClick={() => onChange({ type: 'all', status: 'all', minBeds: 0, maxPrice: Infinity, verified: false })}
          className="text-xs text-primary font-semibold hover:underline"
        >
          Clear All
        </button>
      </div>

      {/* Status */}
      <Section id="status" title="Listing Type">
        <div className="grid grid-cols-2 gap-2">
          {[{ value: 'all', label: 'All' }, { value: 'For Sale', label: 'For Sale' }, { value: 'For Rent', label: 'For Rent' }, { value: 'Off-Plan', label: 'Off-Plan' }].map((opt) => (
            <button
              key={`status-${opt.value}`}
              onClick={() => onChange({ ...filters, status: opt.value })}
              className={`py-2 rounded-lg text-xs font-semibold transition-all ${
                filters.status === opt.value
                  ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Property Type */}
      <Section id="type" title="Property Type">
        <div className="space-y-1.5">
          {propertyTypes.map((t) => (
            <button
              key={`type-filter-${t.value}`}
              onClick={() => onChange({ ...filters, type: t.value })}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                filters.type === t.value
                  ? 'bg-primary/10 text-primary font-semibold' :'text-foreground/70 hover:bg-muted hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Bedrooms */}
      <Section id="beds" title="Bedrooms">
        <div className="flex gap-1.5 flex-wrap">
          {bedroomOptions.map((b) => (
            <button
              key={`beds-${b.value}`}
              onClick={() => onChange({ ...filters, minBeds: b.value })}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                filters.minBeds === b.value
                  ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Price */}
      <Section id="price" title="Max Price">
        <div className="space-y-1.5">
          {priceRanges.map((p) => (
            <button
              key={`price-${p.value}`}
              onClick={() => onChange({ ...filters, maxPrice: p.value })}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                filters.maxPrice === p.value
                  ? 'bg-primary/10 text-primary font-semibold' :'text-foreground/70 hover:bg-muted hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </Section>

      {/* Verified */}
      <Section id="verified" title="Verification">
        <label className="flex items-center gap-3 cursor-pointer">
          <div
            onClick={() => onChange({ ...filters, verified: !filters.verified })}
            className={`w-10 h-5 rounded-full transition-all duration-200 relative cursor-pointer ${
              filters.verified ? 'bg-primary' : 'bg-muted border border-border'
            }`}
          >
            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${filters.verified ? 'left-5' : 'left-0.5'}`} />
          </div>
          <span className="text-sm font-medium text-foreground">OROW Verified Only</span>
        </label>
      </Section>

      {/* Amenities */}
      <Section id="amenities" title="Amenities">
        <div className="space-y-2">
          {amenities.map((a) => (
            <label key={`amenity-${a}`} className="flex items-center gap-2.5 cursor-pointer group">
              <div className="w-4 h-4 rounded border-2 border-border group-hover:border-primary transition-colors flex items-center justify-center">
              </div>
              <span className="text-sm text-foreground/70 group-hover:text-foreground transition-colors">{a}</span>
            </label>
          ))}
        </div>
      </Section>
    </div>
  );
}